import { useState } from "react";
import { motion } from "framer-motion";
import { Target, AlertTriangle, CheckCircle, Settings, Save } from "lucide-react";
import { Label } from "../ui/label";
import { Progress } from "../ui/progress";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const formatCurrency = (value) => {
  const absValue = Math.abs(value);
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(absValue)
    .replace("ZAR", "R");
};

const getStatusColor = (value) => {
  if (value > 0) return "#0891B2"; // Teal - positive
  if (value < 0) return "#DC2626"; // Red - negative
  return "#0F172A"; // Dark - zero
};

const getGapStatus = (gap) => {
  if (gap >= 0) return { color: "#0891B2", icon: CheckCircle };
  return { color: "#DC2626", icon: AlertTriangle };
};

export default function TurnoverTargetEngine({ data, onUpdateSettings, settings }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState({
    office_cost: settings?.office_cost || 216000,
    margin: settings?.margin || 33.3,
  });

  if (!data) return null;

  // Fleet Target comes from vehicle settings (sum of daily breakeven × days)
  // This is passed from backend as mtd_budget
  const fleetTarget = data.fleet_target; // This is now the sum of vehicle targets
  const officeCost = settings?.office_cost || data.office_cost;
  const margin = (settings?.margin || 33.3) / 100;

  // Recalculate based on current settings
  const fleetSurplus = data.fleet_actual - fleetTarget;
  const adjustedOfficeCost = officeCost - fleetSurplus;
  
  const required3rdParty = adjustedOfficeCost > 0 ? adjustedOfficeCost / margin : 0;
  // New Gap calculation: Required + Fleet Actual - Actual 3rd Party
  const thirdPartyGap = required3rdParty + data.fleet_actual - data.actual_3rd_party;
  // Sales Pressure: Required + Fleet Actual - Actual Turnover
  const salesPressure = required3rdParty + data.fleet_actual - data.actual_3rd_party;
  // Progress: Actual Turnover / (Required + Fleet Actual)
  const progressDenominator = required3rdParty + data.fleet_actual;
  const progressPercent = progressDenominator > 0 ? (data.actual_3rd_party / progressDenominator) * 100 : 100;

  const gapStatus = getGapStatus(-thirdPartyGap); // Negative gap is bad (need more money)
  const GapIcon = gapStatus.icon;

  const handleSaveSettings = () => {
    if (onUpdateSettings) {
      onUpdateSettings({
        office_cost: parseFloat(editValues.office_cost) || 216000,
        margin: parseFloat(editValues.margin) || 33.3,
      });
    }
    setEditOpen(false);
  };

  return (
    <div data-testid="turnover-target-engine" className="space-y-4">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#F1F5F9] rounded-xl flex items-center justify-center">
            <Settings className="w-5 h-5 text-[#64748B]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#0F172A]">Turnover Target Engine</h2>
          </div>
        </div>
        
        {/* Controls */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditOpen(true)}
            className="bg-white border-[#0891B2] text-[#0891B2] hover:bg-[#0891B2]/10 hover:text-[#0891B2]"
            data-testid="edit-engine-btn"
          >
            <Settings className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] max-w-sm" data-testid="edit-engine-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] text-xl flex items-center gap-2">
              <Target className="w-5 h-5 text-[#0891B2]" />
              Edit Target Settings
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Configure the target values for calculations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-[#64748B]">Office Cost (R)</Label>
              <Input
                type="number"
                value={editValues.office_cost}
                onChange={(e) => setEditValues({ ...editValues, office_cost: e.target.value })}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                data-testid="edit-office-cost"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#64748B]">Margin (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={editValues.margin}
                onChange={(e) => setEditValues({ ...editValues, margin: e.target.value })}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                data-testid="edit-margin"
              />
            </div>

            <div className="pt-2 border-t border-[#E2E8F0]">
              <p className="text-[#94A3B8] text-xs">
                Fleet Target is calculated from vehicle breakeven settings × days elapsed
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              className="flex-1 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSettings}
              className="flex-1 bg-[#0891B2] hover:bg-[#0E7490] text-white font-semibold"
              data-testid="save-engine-settings-btn"
            >
              <Save className="mr-2 h-4 w-4" />
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Three Blocks in a Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Fleet Recovery Block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm"
        >
          <div className="text-[#64748B] text-sm font-semibold mb-4">Fleet Recovery</div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Fleet Target:</span>
              <span className="text-[#0F172A] font-mono text-lg">{formatCurrency(fleetTarget)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Fleet Actual:</span>
              <span className="font-mono text-lg" style={{ color: getStatusColor(data.fleet_actual) }}>{formatCurrency(data.fleet_actual)}</span>
            </div>
            <div className="pt-2 border-t border-[#E2E8F0] flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Surplus/Shortfall:</span>
              <span
                className="font-mono font-semibold"
                style={{ color: getStatusColor(fleetSurplus) }}
              >
                {fleetSurplus < 0 ? "-" : ""}{formatCurrency(fleetSurplus)}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Office Cost Adjustment Block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm"
        >
          <div className="text-[#64748B] text-sm font-semibold mb-4">Office Cost Adjustment</div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Office Cost:</span>
              <span className="text-[#0F172A] font-mono text-lg">{formatCurrency(officeCost)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Fleet Contribution:</span>
              <span className="font-mono text-lg" style={{ color: fleetSurplus > 0 ? "#0891B2" : "#DC2626" }}>
                {fleetSurplus > 0 ? "-" : "+"}{formatCurrency(Math.abs(fleetSurplus))}
              </span>
            </div>
            <div className="pt-2 border-t border-[#E2E8F0] flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Adjusted Cost:</span>
              <span className="text-[#0F172A] font-mono font-semibold text-lg">
                {formatCurrency(Math.max(0, adjustedOfficeCost))}
              </span>
            </div>
          </div>
        </motion.div>

        {/* 3rd Party Turnover Block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm"
        >
          <div className="text-[#64748B] text-sm font-semibold mb-4">3rd Party Turnover</div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Required (@{(margin * 100).toFixed(1)}%):</span>
              <span className="text-[#0F172A] font-mono text-lg">{formatCurrency(required3rdParty)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Fleet Actual:</span>
              <span className="font-mono text-lg" style={{ color: getStatusColor(data.fleet_actual) }}>{formatCurrency(data.fleet_actual)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Actual Turnover:</span>
              <span className="font-mono text-lg" style={{ color: getStatusColor(data.actual_3rd_party) }}>{formatCurrency(data.actual_3rd_party)}</span>
            </div>
            <div className="pt-2 border-t border-[#E2E8F0] flex justify-between items-center">
              <span className="text-[#64748B] text-sm">Gap:</span>
              <span className="font-mono font-semibold" style={{ color: getStatusColor(-thirdPartyGap) }}>
                {formatCurrency(thirdPartyGap)}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* 3rd Party Progress Bar - Full Width Below */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-[#64748B] text-sm font-medium">3rd Party Progress</span>
          <span className="text-[#0F172A] font-mono text-sm">
            {formatCurrency(data.actual_3rd_party)} / {formatCurrency(required3rdParty)}
          </span>
        </div>
        
        <div className="relative mb-4">
          <Progress 
            value={Math.min(progressPercent, 100)} 
            className="h-6 bg-[#F1F5F9]"
            data-testid="progress-bar"
          />
          <div className="absolute inset-0 flex items-center justify-center text-sm font-bold" style={{ color: progressPercent > 50 ? '#ffffff' : '#0891B2' }}>
            {progressPercent.toFixed(1)}%
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#64748B] text-sm">Sales Pressure Remaining:</span>
          <div className="flex items-center gap-2">
            <span 
              className="font-mono font-semibold text-lg"
              style={{ color: salesPressure > 0 ? "#DC2626" : "#0891B2" }}
            >
              {salesPressure > 0 ? formatCurrency(salesPressure) : "Target Met!"}
            </span>
            {salesPressure > 0 && <AlertTriangle className="w-5 h-5 text-[#DC2626]" />}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
